<template>
  <div class="timeline">
    <div class="d-flex flex-column">
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0" style="font-size: 16px">Timeline</h4>
        <div class="d-flex" style="gap: 15px">
          <b-button variant="outline-primary" size="sm" @click="addChartNote">
            <strong>+</strong> Chart Note
          </b-button>
          <b-dropdown
            ref="form-dropdown"
            variant="outline-primary"
            size="sm"
            menu-class="form-dropdown"
            toggle-tag="div"
            boundary="window"
            right
          >
            <template #button-content> <strong>+</strong> Document </template>
            <b-form-group label="Document type">
              <v-select
                :options="documentTypesExpress"
                v-model="expressDocument"
              />
            </b-form-group>
          </b-dropdown>
        </div>
      </div>
      <div class="d-flex flex-column border-top p-2 mt-2"></div>
      <div
        class="entries-group"
        v-for="[key, entries] in groupedEntries"
        :key="key"
      >
        <div class="my-2">
          <strong class="date px-2 py-1 rounded">{{ key }}</strong>
        </div>
        <div class="d-flex flex-column">
          <eoc-view-summary-timeline-entry
            v-for="entry in entries"
            :key="entry.id"
            :entry="entry"
            @onViewDocument="viewDocument"
            @onEditDocument="editDocument"
            @onDeleteDocument="deleteDocument"
            @onViewGroupNote="viewGroupNote"
            @onEditChartNote="editChartNote"
            @onDeleteChartNote="deleteChartNote"
          />
        </div>
      </div>
      <div v-if="showLoadMoreButton" class="d-flex justify-content-center my-3">
        <b-button
          variant="outline-secondary"
          class="load-more"
          @click="fetchEntries()"
        >
          Load More...
        </b-button>
      </div>
    </div>
    <document-file-add-edit-modal
      v-if="showEditFileModal"
      v-model="showEditFileModal"
      :eocId="eoc.id"
      :clientId="eoc.patientId"
      :formId="formId"
      :submitId.sync="entityId"
      @onModify="onModifyEntity"
    />
    <document-add-edit-modal
      v-if="showAddEditDocumentModal"
      v-model="showAddEditDocumentModal"
      :eocId="eoc.id"
      :clientId="eoc.patientId"
      :formId="formId"
      :submitId="entityId"
      @onModify="onModifyEntity"
    />
    <document-group-add-edit-modal
      v-if="showAddEditDocumentGroupModal"
      v-model="showAddEditDocumentGroupModal"
      :eocId="eoc.id"
      :clientId="eoc.patientId"
      :formGroupId="formGroupId"
      :submitId="entityId"
      @onModify="onModifyEntity"
    />
    <eoc-view-add-edit-chart-note-modal
      v-if="showChartNoteModal"
      v-model="showChartNoteModal"
      :chartNoteId="entityId"
      :eocId="eoc.id"
      :eocLocationId = "eoc.locationId"
      @onModify="onModifyEntity"
    />
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import EocViewSummaryTimelineEntry from "./EocViewSummaryTimelineEntry.vue";
import DocumentFileAddEditModal from "./document/DocumentFileAddEditModal.vue";
import DocumentAddEditModal from "./document/DocumentAddEditModal.vue";
import DocumentGroupAddEditModal from "./document_group/DocumentGroupAddEditModal.vue";
import EocViewAddEditChartNoteModal from "./EocViewAddEditChartNoteModal.vue";
import timelineServer from "../../server/timeline";
import documentServer from "../../server/document";
import formSubmitterServer from "../../server/formSubmitter";
import scheduleEventServer from "../../server/scheduleEvent";
import chartNoteServer from "../../server/chartNote";
import { groupBy } from "../../helpers/general";
import { getFormBuilderComponents } from "../../../../Breeze.FormBuilder.Frontend/helpers/formBuilder";

export default {
  name: "EocViewSummaryTimeline",
  components: {
    EocViewSummaryTimelineEntry,
    DocumentFileAddEditModal,
    DocumentAddEditModal,
    DocumentGroupAddEditModal,
    EocViewAddEditChartNoteModal
  },
  props: ["eoc", "showing"],
  data() {
    return {
      entriesCount: 0,
      entries: [],
      page: 0,
      pageSize: 25,
      entityId: null,
      formId: null,
      formGroupId: null,
      showEditFileModal: false,
      showAddEditDocumentModal: false,
      showAddEditDocumentGroupModal: false,
      showChartNoteModal: false,
      expressDocument: null
    };
  },
  mounted() {
    this.fetchEntries();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    async fetchEntries(pageSize = this.pageSize) {
      const response = await timelineServer.query({
        episodeOfCareId: [this.eoc.id],
        page: this.page,
        pageSize
      });

      this.entries = this.entries.concat(response.records);
      this.entriesCount = response.count;
      this.page += 1;
    },
    resetEntries() {
      this.entries = [];
      this.entriesCount = 0;
      this.page = 0;
    },
    async createNewExpressDocument() {
      this.$refs["form-dropdown"].hide();
      this.entityId = null;
      const formId = this.expressDocument.value;
      if (this.expressDocument.type == "form") {
        this.formId = formId;
        this.form = await this.getFormBuilderFormById(this.formId);
        if (getFormBuilderComponents(this.form.current.content).length > 0) {
          this.showAddEditDocumentModal = true;
        } else {
          this.showEditFileModal = true;
        }
      } else {
        this.formGroupId = formId;
        this.showAddEditDocumentGroupModal = true;
      }

      this.expressDocument = null;
    },
    async viewDocument(entityId) {
      const document = await formSubmitterServer.get(entityId);
      let url;
      if (document.mode == "File") {
        url = formSubmitterServer.viewUrl(entityId);
        window.open(url, "_blank");
        return;
      }

      if (document.category == "masterTreatmentPlan") {
        url = documentServer.treatmentPlanDocumentViewUrl(entityId);
      } else if (document.groupSessionId) {
        url = scheduleEventServer.groupSessionDocumentViewUrl(entityId);
      } else {
        url = documentServer.documentsViewUrl([entityId]);
      }

      fetch(url.url, { headers: url.headers }).then(response => {
        const clone = response.clone();

        let win = window.open("", "_blank");
        clone.text().then(html => {
          win.document.body.innerHTML = html;
        });
      });
    },
    async editDocument(entityId) {
      const document = await formSubmitterServer.get(entityId);
      this.entityId = entityId;
      this.formId = document.formId;

      if (document.mode == "File") {
        this.showEditFileModal = true;
      } else {
        this.showAddEditDocumentModal = true;
      }
    },
    async deleteDocument(entityId) {
      this.$dialog
        .confirm(
          {
            title: "Are you sure you want to delete this document?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          await formSubmitterServer.delete(entityId);
          const pageSize = Math.max(this.entries.length, this.pageSize);
          this.showAlertSuccess();
          this.resetEntries();
          this.fetchEntries(pageSize);
        })
        .catch(function() {});
    },
    viewGroupNote(noteId) {
      const url = scheduleEventServer.groupSessionDocumentViewUrl(noteId);
      window.open(url, "_blank");
    },
    addChartNote() {
      this.entityId = null;
      this.showChartNoteModal = true;
    },
    editChartNote(entityId) {
      this.entityId = entityId;
      this.showChartNoteModal = true;
    },
    async deleteChartNote(entityId) {
      this.$dialog
        .confirm(
          {
            title: "Are you sure you want to delete this chart note?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          await chartNoteServer.delete(entityId);
          const pageSize = Math.max(this.entries.length, this.pageSize);
          this.showAlertSuccess();
          this.resetEntries();
          this.fetchEntries(pageSize);
        })
        .catch(function() {});
    },
    onModifyEntity() {
      this.showEditFileModal = false;
      this.showAddEditDocumentModal = false;
      this.showAddEditDocumentGroupModal = false;
      this.showAddEditGroupNoteModal = false;
      this.showChartNoteModal = false;

      const pageSize = Math.max(this.entries.length, this.pageSize);
      this.resetEntries();
      this.fetchEntries(pageSize);
    }
  },
  computed: {
    ...mapGetters("client", ["listeners"]),
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    ...mapGetters("client", ["formsCurrentLocation"]),
    forms() {
      return this.formsCurrentLocation
        .filter(form =>
          this.hasCurrentUserDocumentPermission(form.id, "hasEntryAccess")
        )
        .map(form => ({
          value: form.id,
          label: form.name,
          type: "form"
        }));
    },
    ...mapGetters("client", ["formGroupsCurrentLocation"]),
    ...mapGetters("formBuilder", ["getFormBuilderFormById"]),
    formGroups() {
      return this.formGroupsCurrentLocation
        .filter(formGroup =>
          this.hasCurrentUserDocumentPermission(formGroup.id, "hasEntryAccess")
        )
        .map(form => ({
          value: form.id,
          label: form.name,
          type: "formGroup"
        }));
    },
    documentTypesExpress() {
      return [...this.forms, ...this.formGroups].sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      );
    },
    groupedEntries() {
      const sortedEntries = this.entries.slice().sort((a, b) => {
        return new Date(b.dateTime) - new Date(a.dateTime);
      });

      return groupBy(sortedEntries, v =>
        moment(v.dateTime).format("YYYY MMM DD")
      );
    },
    showLoadMoreButton() {
      return this.entriesCount > this.entries.length;
    }
  },
  watch: {
    expressDocument(newValue) {
      if (newValue) {
        this.createNewExpressDocument();
      }
    },
    showing(newValue) {
      if (newValue) {
        const pageSize = Math.max(this.entries.length, this.pageSize);
        this.resetEntries();
        this.fetchEntries(pageSize);
      }
    }
  }
};
</script>

<style scoped>
.date {
  display: inline-block;
  background-color: #f5f8fc;
  margin-left: 105px;
  transform: translateX(-50%);
}

.timeline >>> .timeline-entry:first-of-type {
  padding-top: 1.5rem;
}
.timeline >>> .timeline-entry:last-of-type {
  padding-bottom: 1.5rem;
}

.timeline >>> .btn-outline-primary {
  border-color: transparent !important;
}
.timeline >>> .form-dropdown {
  min-width: 300px;
  padding: 0.75rem 1rem;
  left: -17px;
}

.load-more {
  width: 9em;
}
</style>
